// Bottom-sheet add flow for the MCM shopping list. Zero-friction by
// design: text field + qty stepper + Add. No category, no location,
// no brand, no expiry. The user just jots what they need.
//
// Smart-but-silent canonical pairing: at submit time we run
// inferCanonicalFromName on the typed text. If it resolves (e.g.,
// "apples" → `apple`), we attach `ingredientId` + the canonical's
// `category` to the row so the eventual check-off → kitchen flow
// has enough signal to route the item to the right tile (Produce,
// Dairy, etc.) without ever asking the user. If inference misses,
// the row is still saved as plain text — it just lands in the
// fallback "Other" aisle on the shopping screen and the catch-all
// "Miscellaneous" tile after check-off, which the user can re-tile
// later. The tradeoff: zero data-entry tax for the common case at
// the cost of imperfect classification on novel items.

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTheme } from "./theme";
import { font, radius, shadow } from "./tokens";
import { withAlpha } from "./primitives";
import {
  inferCanonicalFromName,
  findIngredient,
} from "../../data/ingredients";

export function MCMShoppingAdd({ onClose, onAdd, onUseWizard }) {
  const { theme } = useTheme();
  const [name, setName] = useState("");
  const [qty, setQty]   = useState(1);
  const inputRef = useRef(null);

  useEffect(() => {
    // Autofocus so the user can start typing the moment the sheet
    // mounts. Small delay so the keyboard doesn't fight the
    // sheet's enter animation on iOS.
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  // Esc closes — keyboard parity with the rest of the MCM sheets.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function submit() {
    const cleaned = name.trim();
    if (!cleaned) return;

    // Silent canonical inference. inferCanonicalFromName matches
    // against the bundled alias map ("apples" / "apple" / "Granny
    // Smith apples" → `apple` slug). Returns null when nothing
    // matches — that's fine, the row still saves as plain text.
    const canonicalId = inferCanonicalFromName(cleaned);
    const ing = canonicalId ? findIngredient(canonicalId) : null;
    const inferredCategory = ing?.category || "pantry";

    const id = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    onAdd?.({
      id,
      name: cleaned,
      emoji: ing?.emoji || "🛒",
      amount: qty,
      unit: "",
      category: inferredCategory,
      source: "manual",
      ingredientId: canonicalId || null,
      priceCents: null,
    });
    onClose?.();
  }

  function handleKey(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  }

  const ready = name.trim().length > 0;

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label="Add to list"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.18, ease: "easeIn" } }}
      transition={{ duration: 0.16, ease: "easeOut" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: "rgba(20,12,4,0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%", transition: { duration: 0.22, ease: "easeIn" } }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          // glassFillHeavy (per-theme bright sheet) — paper is DARK
          // on night/dawn/dusk and the whole sheet renders as a
          // dark slab. Same rule as cards/tiles.
          background: theme.color.glassFillHeavy,
          color: theme.color.ink,
          borderTopLeftRadius: radius.sheet,
          borderTopRightRadius: radius.sheet,
          padding: "16px 22px calc(28px + env(safe-area-inset-bottom))",
          boxShadow: shadow.lift,
          // Inset highlight on the top edge so the sheet reads as a
          // raised glass surface, matching the rest of the MCM
          // sheets.
          borderTop: `1px solid ${withAlpha("#ffffff", 0.85)}`,
        }}
      >
        {/* Drag handle — pure visual, the sheet doesn't actually
            swipe. Signals "this is dismissable" to iOS users. */}
        <div
          aria-hidden
          style={{
            width: 44,
            height: 4,
            borderRadius: 2,
            background: theme.color.hairline,
            margin: "0 auto 14px",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 14,
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: font.mono,
                fontSize: 10,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: theme.color.inkMuted,
              }}
            >
              Add to list
            </div>
            <div
              style={{
                marginTop: 2,
                fontFamily: font.detail,
                fontStyle: "italic",
                fontSize: 14,
                color: theme.color.inkFaint,
                lineHeight: 1.3,
              }}
            >
              Just the name — we'll sort the rest at the store.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30,
              height: 30,
              borderRadius: 15,
              border: `1px solid ${theme.color.hairline}`,
              background: theme.color.glassFill,
              color: theme.color.inkMuted,
              fontFamily: font.mono,
              fontSize: 14,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKey}
          placeholder="corn, apples, beer…"
          style={{
            width: "100%",
            padding: "16px 18px",
            background: theme.color.glassFill,
            border: `1px solid ${theme.color.hairline}`,
            borderRadius: radius.field,
            color: theme.color.ink,
            fontFamily: font.itemName,
            fontSize: 16, fontWeight: 600, fontStyle: "italic",
            outline: "none",
            boxSizing: "border-box",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            margin: "16px 4px 0",
          }}
        >
          <div
            style={{
              fontFamily: font.mono,
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: theme.color.inkMuted,
              flex: 1,
            }}
          >
            How many
          </div>
          <button
            type="button"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            aria-label="Decrease quantity"
            style={qtyBtnStyle(theme)}
          >
            −
          </button>
          <div
            style={{
              minWidth: 38,
              textAlign: "center",
              fontFamily: font.mono,
              fontSize: 18,
              color: theme.color.ink,
            }}
          >
            ×{qty}
          </div>
          <button
            type="button"
            onClick={() => setQty((q) => q + 1)}
            aria-label="Increase quantity"
            style={qtyBtnStyle(theme)}
          >
            +
          </button>
        </div>

        {onUseWizard && (
          <button
            type="button"
            onClick={onUseWizard}
            style={{
              marginTop: 16,
              width: "100%",
              padding: "10px 12px",
              background: "transparent",
              border: "none",
              color: theme.color.inkMuted,
              fontFamily: font.detail,
              fontStyle: "italic",
              fontSize: 13,
              lineHeight: 1.3,
              textAlign: "center",
              cursor: "pointer",
              textDecoration: "underline",
              textUnderlineOffset: 3,
              textDecorationColor: theme.color.hairline,
            }}
          >
            Need brand, size, or cut? Use the wizard →
          </button>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: onUseWizard ? 12 : 20 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: "14px",
              background: "transparent",
              border: `1px solid ${theme.color.hairline}`,
              color: theme.color.inkMuted,
              borderRadius: radius.pill,
              fontFamily: font.mono,
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!ready}
            style={{
              flex: 2,
              padding: "14px",
              background: ready ? theme.color.burnt : withAlpha(theme.color.burnt, 0.35),
              border: "none",
              // Hardcoded off-white for guaranteed contrast on the
              // burnt-orange CTA. theme.color.paper is dark on
              // dawn/dusk/night themes, which collapsed contrast
              // against the orange bg.
              color: "#FFFCF4",
              borderRadius: radius.pill,
              fontFamily: font.mono,
              fontSize: 12,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontWeight: 600,
              cursor: ready ? "pointer" : "not-allowed",
              transition: "background 160ms ease",
            }}
          >
            Add to list
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function qtyBtnStyle(theme) {
  return {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: `1px solid ${theme.color.hairline}`,
    background: theme.color.glassFill,
    color: theme.color.ink,
    fontFamily: font.mono,
    fontSize: 18,
    lineHeight: 1,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

export default MCMShoppingAdd;
